// The signed-in person's own menu: who am I / what role, text size, change
// password, sign out.
import { Api } from "../../platform/api.js";
import { useT } from "../i18n.jsx";
import { KeyIcon, LogOutIcon, T, TextSizeIcon } from "../theme.jsx";
import { toast } from "./collab.jsx";
import { Button, Field, Modal, Notice } from "./ui.jsx";

const { useState } = React;

const TEXT_SCALES = [1, 1.15, 1.3];
function readTextScale() {
  try { const v = parseFloat(localStorage.getItem("rb:textScale")); return TEXT_SCALES.includes(v) ? v : 1; } catch (e) { return 1; }
}
// CSS zoom scales the whole layout (the app's sizes are inline pixel values,
// so changing the root font-size alone wouldn't reach them)
function applyTextScale(v) {
  const root = document.getElementById("root");
  if (root) root.style.zoom = v === 1 ? "" : String(v);
  try { localStorage.setItem("rb:textScale", String(v)); } catch (e) {}
}

function AccountMenu({ viewer, onSignOut, dark = true, compact = false }) {
  const { t } = useT();
  const [open, setOpen] = useState(false);
  const [pwOpen, setPwOpen] = useState(false);
  const [scale, setScale] = useState(readTextScale);
  const fg = dark ? T.darkText : T.text;
  return (
    <>
      <button className="rb-btn" type="button" onClick={() => setOpen(true)} aria-haspopup="dialog" aria-label={`${t("accountMenu")}: ${viewer.name}, ${viewer.roleLabel}`}
        style={{ display: "flex", alignItems: "center", gap: 8, background: "transparent", border: "none", cursor: "pointer", padding: 4, borderRadius: 8, color: fg, minWidth: 0, textAlign: "left" }}>
        <span aria-hidden="true" style={{ width: 28, height: 28, borderRadius: "50%", background: dark ? T.darkChip : T.panel2, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 800, flex: "0 0 auto" }}>
          {(viewer.name || "?").slice(0, 1).toUpperCase()}
        </span>
        {!compact && (
          <span style={{ minWidth: 0 }}>
            <span style={{ display: "block", fontSize: 12.5, fontWeight: 700, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{viewer.name}</span>
            <span style={{ display: "block", fontSize: 11.5, fontWeight: 800, color: T.accent }}>{viewer.roleLabel}</span>
          </span>
        )}
      </button>
      {open && (
        <Modal title={viewer.name} onClose={() => setOpen(false)} closeLabel={t("close")} width={400}>
          <div style={{ fontSize: 13, color: T.subtext, marginBottom: 14 }}>
            {viewer.email} · <b style={{ color: T.text }}>{viewer.roleLabel}</b>
          </div>
          <fieldset style={{ border: `1px solid ${T.border}`, borderRadius: 10, padding: "10px 12px", marginBottom: 14 }}>
            <legend style={{ fontSize: 13, fontWeight: 700, padding: "0 4px", display: "flex", alignItems: "center", gap: 5 }}><TextSizeIcon size={14} /> {t("textSize")}</legend>
            <div style={{ display: "flex", gap: 6 }}>
              {TEXT_SCALES.map((v) => (
                <button key={v} type="button" className="rb-btn" aria-pressed={scale === v} onClick={() => { setScale(v); applyTextScale(v); }}
                  style={{ flex: 1, padding: "8px 0", borderRadius: 8, border: `1px solid ${scale === v ? T.accent : T.border}`, background: scale === v ? T.accent : "#fff", color: T.text, fontWeight: 800, fontSize: 12 + (v - 1) * 20, cursor: "pointer" }}>
                  {Math.round(v * 100)}%
                </button>
              ))}
            </div>
          </fieldset>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {viewer.hasPassword && (
              <Button variant="secondary" onClick={() => { setOpen(false); setPwOpen(true); }} style={{ display: "flex", alignItems: "center", gap: 6, justifyContent: "center" }}>
                <KeyIcon size={14} /> {t("changePassword")}
              </Button>
            )}
            <Button variant="danger" onClick={onSignOut} style={{ display: "flex", alignItems: "center", gap: 6, justifyContent: "center" }}>
              <LogOutIcon size={14} /> {t("signOut")}
            </Button>
            <Button variant="ghost" onClick={async () => { try { await Api.logoutEverywhere(); } catch (e) {} onSignOut(); }}
              style={{ display: "flex", alignItems: "center", gap: 6, justifyContent: "center", fontSize: 12.5 }}>
              {t("signOutAllDevices")}
            </Button>
          </div>
        </Modal>
      )}
      {pwOpen && <ChangePasswordModal onClose={() => setPwOpen(false)} />}
    </>
  );
}

function ChangePasswordModal({ onClose }) {
  const { t } = useT();
  const [f, setF] = useState({ current: "", next: "", confirm: "" });
  const [error, setError] = useState("");
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const submit = async (e) => {
    e.preventDefault();
    if (f.next !== f.confirm) { setError(t("passwordsDontMatch")); return; }
    try { await Api.changePassword(f.current, f.next); toast(t("passwordChanged")); onClose(); }
    catch (err) { setError(err.message); }
  };
  return (
    <Modal title={t("changePassword")} onClose={onClose} closeLabel={t("close")} width={420}>
      {error && <Notice>{error}</Notice>}
      <form onSubmit={submit}>
        <Field label={t("currentPassword")} inputProps={{ type: "password", autoComplete: "current-password", value: f.current, onChange: set("current") }} />
        <Field label={t("newPassword")} hint={t("passwordHint")} inputProps={{ type: "password", autoComplete: "new-password", value: f.next, onChange: set("next") }} />
        <Field label={t("confirmPasswordLabel")} inputProps={{ type: "password", autoComplete: "new-password", value: f.confirm, onChange: set("confirm") }} />
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <Button variant="ghost" onClick={onClose}>{t("cancel")}</Button>
          <Button type="submit" disabled={!f.current || f.next.length < 10}>{t("save")}</Button>
        </div>
      </form>
    </Modal>
  );
}

export { AccountMenu, applyTextScale, readTextScale };
